import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Vote, CheckCircle2, Clock, CalendarDays, MoreVertical, Trash2, Edit, Users, X } from 'lucide-react';
import { confirmAction } from '../../utils/confirmToast';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './AdminPolls.module.css';

const AdminPolls = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVotersModal, setShowVotersModal] = useState(false);
  const [selectedVotersData, setSelectedVotersData] = useState(null);

  useEffect(() => {
    fetchPolls();
  }, []);

  const fetchPolls = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/community/polls');
      setPolls(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    endDate: '',
    options: [{ id: Date.now(), text: '' }, { id: Date.now() + 1, text: '' }]
  });

  const handleOptionChange = (id, text) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map(opt => opt.id === id ? { ...opt, text } : opt)
    }));
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { id: Date.now(), text: '' }]
    }));
  };

  const removeOption = (id) => {
    if (formData.options.length <= 2) return; // Minimum 2 options
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter(opt => opt.id !== id)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate options
    const validOptionsText = formData.options.map(opt => opt.text.trim()).filter(text => text !== '');
    if (validOptionsText.length < 2) {
      toast.error('Please provide at least 2 valid options.');
      return;
    }

    try {
      const submitData = {
        title: formData.title,
        description: formData.description,
        endDate: formData.endDate,
        options: validOptionsText
      };

      const { data } = await api.post('/community/polls', submitData);
      setPolls([data, ...polls]);
      setIsDrawerOpen(false);
      setFormData({
        title: '',
        description: '',
        endDate: '',
        options: [{ id: Date.now(), text: '' }, { id: Date.now() + 1, text: '' }]
      });
      toast.success('Poll created successfully!');
    } catch (err) {
      toast.error('Failed to create poll');
    }
  };

  const handleClose = async (id) => {
    confirmAction("Are you sure you want to close this poll? Voters will no longer be able to cast votes.", async () => {
      try {
        const { data } = await api.put(`/community/polls/${id}/close`);
        setPolls(polls.map(p => p._id === id ? data : p));
        toast.success('Poll closed successfully');
      } catch (err) {
        toast.error('Failed to close poll');
      }
    });
  };

  const handleDelete = async (id) => {
    confirmAction("Are you sure you want to delete this poll?", async () => {
      try {
        await api.delete(`/community/polls/${id}`);
        setPolls(polls.filter(p => p._id !== id));
        toast.success('Poll deleted successfully');
      } catch (err) {
        toast.error('Failed to delete poll');
      }
    });
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Polls & Voting Management" 
        breadcrumb={['Community', 'Polls']} 
        actionButton={
          <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
            <Plus size={16} /> Create New Poll
          </button>
        }
      />

      <div className={styles.grid}>
        {loading ? <p style={{padding: '0 24px'}}>Loading polls...</p> : polls.map(poll => {
          const isActive = poll.status === 'Active';

          return (
            <div key={poll._id} className={`${styles.card} ${!isActive ? styles.closedCard : ''}`}>
              <div className={styles.cardHeader}>
                <div className={styles.titleArea}>
                  <span className={`${styles.badge} ${isActive ? styles.badgeActive : styles.badgeClosed}`}>
                    {isActive ? <Clock size={12} /> : <CheckCircle2 size={12} />}
                    {poll.status}
                  </span>
                  <h3>{poll.title}</h3>
                </div>
                <div className={styles.actions}>
                  {isActive && (
                    <button className={styles.iconBtn} title="Close Poll" onClick={() => handleClose(poll._id)}>
                      <CheckCircle2 size={16} />
                    </button>
                  )}
                  <button className={styles.iconBtn} title="Delete" onClick={() => handleDelete(poll._id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              
              <p className={styles.description}>{poll.description}</p>
              
              <div className={styles.meta}>
                <span><CalendarDays size={14} /> End Date: {poll.endDate}</span>
                <span><Vote size={14} /> {poll.totalVotes} Total Votes</span>
              </div>

              <div className={styles.optionsList}>
                {poll.options.map(opt => {
                  const percentage = poll.totalVotes > 0 
                    ? Math.round((opt.votes / poll.totalVotes) * 100) 
                    : 0;
                  
                  const visibleVoters = opt.voters ? opt.voters.slice(0, 3) : [];
                  
                  return (
                    <div 
                      key={opt.id} 
                      className={styles.waOption}
                      onClick={() => {
                        if (opt.votes > 0) {
                          setSelectedVotersData({
                            pollTitle: poll.title,
                            optionText: opt.text,
                            voters: opt.voters || []
                          });
                          setShowVotersModal(true);
                        }
                      }}
                      style={{ cursor: opt.votes > 0 ? 'pointer' : 'default' }}
                      title={opt.votes > 0 ? "Click to view voters" : ""}
                    >
                      {/* Background Progress Bar */}
                      <div 
                        className={`${styles.waProgressBg} ${!isActive ? styles.waProgressClosed : ''}`} 
                        style={{ width: `${percentage}%` }}
                      ></div>
                      
                      <div className={styles.waOptionContent}>
                        <div className={styles.waLeftArea}>
                          <div className={styles.waRadio}></div>
                          <span className={styles.waText}>{opt.text}</span>
                        </div>
                        
                        <div className={styles.waRightArea}>
                          <div className={styles.waAvatars}>
                            {visibleVoters.map((v, i) => {
                              const imgUrl = v.profileImage ? (v.profileImage.startsWith('http') ? v.profileImage : `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${v.profileImage}`) : null;
                              return (
                                <div key={i} className={styles.waAvatar}>
                                  {imgUrl && (
                                    <img 
                                      src={imgUrl} 
                                      alt="" 
                                      style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover'}}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  )}
                                  <span style={{ display: imgUrl ? 'none' : 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                    {v.fullName ? v.fullName.charAt(0).toUpperCase() : 'U'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          
                          {opt.votes > 0 && (
                            <span className={styles.waVoteCount}>{opt.votes}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <Drawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        title="Create New Poll"
      >
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Poll Title</label>
            <input 
              value={formData.title} 
              onChange={e => setFormData({...formData, title: e.target.value})} 
              required 
              placeholder="e.g. New Security Agency" 
            />
          </div>
          
          <div className="formGroup">
            <label>Description (Optional)</label>
            <textarea 
              value={formData.description} 
              onChange={e => setFormData({...formData, description: e.target.value})} 
              rows="3"
              placeholder="Provide more context..." 
            />
          </div>

          <div className="formGroup">
            <label>End Date</label>
            <input 
              type="date"
              value={formData.endDate} 
              onChange={e => setFormData({...formData, endDate: e.target.value})} 
              required 
            />
          </div>

          <div className="formGroup">
            <label>Poll Options</label>
            <div className={styles.optionInputs}>
              {formData.options.map((opt, index) => (
                <div key={opt.id} className={styles.optionInputRow}>
                  <input 
                    value={opt.text} 
                    onChange={e => handleOptionChange(opt.id, e.target.value)} 
                    placeholder={`Option ${index + 1}`}
                    required={index < 2} // First two options are mandatory
                  />
                  {formData.options.length > 2 && (
                    <button type="button" className={styles.removeOptBtn} onClick={() => removeOption(opt.id)}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" className={styles.addOptBtn} onClick={addOption}>
              <Plus size={14} /> Add Another Option
            </button>
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Create Poll</button>
          </div>
        </form>
      </Drawer>

      {/* Voters Modal */}
      {showVotersModal && selectedVotersData && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <div>
                <h3>{selectedVotersData.optionText}</h3>
                <p>Voters for this option</p>
              </div>
              <button onClick={() => setShowVotersModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className={styles.voterList}>
              {selectedVotersData.voters.map(voter => {
                const imgUrl = voter.profileImage ? (voter.profileImage.startsWith('http') ? voter.profileImage : `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${voter.profileImage}`) : null;
                return (
                  <div key={voter._id} className={styles.voterItem}>
                    <div className={styles.voterAvatar}>
                      {imgUrl && (
                        <img 
                          src={imgUrl} 
                          alt="" 
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      )}
                      <span style={{ display: imgUrl ? 'none' : 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                        {voter.fullName ? voter.fullName.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                    <div className={styles.voterInfo}>
                      <span className={styles.voterName}>{voter.fullName || 'Unknown User'}</span>
                      <span className={styles.voterFlat}>Flat {voter.flatNumber} {voter.wing && `• Block ${voter.wing}`}</span>
                    </div>
                  </div>
                );
              })}
              {selectedVotersData.voters.length === 0 && (
                <p style={{textAlign: 'center', color: '#64748b', padding: '12px', fontSize: '13px', lineHeight: '1.5'}}>
                  No specific user data is available for these votes. <br/><br/>
                  <i>Note: Votes cast before this tracking feature was added are counted numerically but do not display user profiles.</i>
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPolls;
